import { Alert, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React, { FunctionComponent, useMemo } from 'react'

import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { numberSorter, stringSorter } from '../../utils/generalTableHelpers'
import { sparsePagination } from '../../utils/layoutConst'
import { propertyListDetailFilterOptions as _options } from '../../utils/propertyListTableHelpers'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { useColumnOptionFilter } from '../../utils/useColumnOptionFilter'
import type { PropertyListDetailRow as Item } from '../../utils/useFetchPropertyListDetailBuilding'
import { useFetchPropertyListBuildingDetail } from '../../utils/useFetchPropertyListDetailBuilding'
import { useTableLocale } from '../../utils/useTableLocale'
import { Money } from '../Money/Money'
import { PropertyListDetailPropertyDetailLink } from '../PropertyListDetailPropertyDetailLink/PropertyListDetailPropertyDetailLink'

export interface PropertyListBuildingDetailTableProps {
  whereClause: string
}

const scrollX = { x: 'max-content' }
const tableId = 'property-list-detail'

export const PropertyListBuildingDetailTable: FunctionComponent<PropertyListBuildingDetailTableProps> =
  ({ whereClause }) => {
    const {
      data: dataSource,
      loading,
      error,
    } = useFetchPropertyListBuildingDetail(whereClause)

    const locale = useTableLocale(loading)

    const { rangeFilter, searchFilter } = useColumnFilters<Item>()

    const { getColumnOptionFilter: getColumnOptionFilterVytapeni } =
      useColumnOptionFilter<Item>(undefined, _options.vytapeni)
    const { getColumnOptionFilter: getColumnOptionFilterVytah } =
      useColumnOptionFilter<Item>(undefined, _options.vytah)
    const { getColumnOptionFilter: getColumnOptionFilterVodovod } =
      useColumnOptionFilter<Item>(undefined, _options.vodovod)
    const { getColumnOptionFilter: getColumnOptionFilterPlyn } =
      useColumnOptionFilter<Item>(undefined, _options.plyn)
    const { getColumnOptionFilter: getColumnOptionFilterKanalizace } =
      useColumnOptionFilter<Item>(undefined, _options.kanalizace)
    const { getColumnOptionFilter: getColumnOptionFilterDruhKonstrukce } =
      useColumnOptionFilter<Item>(undefined, _options.druhKonstrukce)
    const { getColumnOptionFilter: getColumnOptionFilterZpusobVyuziti } =
      useColumnOptionFilter<Item>(undefined, _options.zpusobVyuziti)

    const columns = useMemo<ColumnsType<Item>>(
      () => [
        {
          title: 'typ st.',
          dataIndex: 'typstavebnihoobjektuText',
          sorter: numberSorter('typstavebnihoobjektukod'),
          width: 70,
        },
        {
          title: 'zp. využití',
          dataIndex: 'zpusobvyuzitiText',
          width: 150,
          sorter: numberSorter('zpusobvyuzitikod'),
          ...getColumnOptionFilterZpusobVyuziti('zpusobvyuzitikod'),
        },
        {
          title: 'část obce',
          dataIndex: 'castobceText',
          width: 160,
          sorter: stringSorter('castobceText'),
          ...searchFilter('castobceText'),
        },
        {
          title: 'druh konstr.',
          dataIndex: 'druhkonstrukceText',
          width: 140,
          sorter: stringSorter('druhkonstrukceText'),
          ...getColumnOptionFilterDruhKonstrukce('druhkonstrukcekod'),
        },
        {
          title: 'byty',
          dataIndex: 'pocetbytu',
          className: 'text-right',
          width: 50,
          sorter: numberSorter('pocetbytu'),
          ...rangeFilter('pocetbytu'),
        },
        {
          title: 'podlaží',
          dataIndex: 'pocetpodlazi',
          className: 'text-right',
          width: 50,
          sorter: numberSorter('pocetpodlazi'),
          ...rangeFilter('pocetpodlazi'),
        },
        {
          title: 'obest. prostor',
          dataIndex: 'obestavenyprostor',
          width: 130,
          className: 'text-right',
          sorter: numberSorter('obestavenyprostor'),
          ...rangeFilter('obestavenyprostor'),
          render: (v: number | null) => (
            <Money amount={v} decimalNumbers={0} symbol="m³" noData="---" />
          ),
        },
        {
          title: 'zast. plocha',
          dataIndex: 'zastavenaplocha',
          width: 120,
          className: 'text-right',
          sorter: numberSorter('zastavenaplocha'),
          ...rangeFilter('zastavenaplocha'),
          render: (v: number | null, record) => (
            <Money
              amount={v || record['st_area(shape)']}
              decimalNumbers={0}
              noData="---"
              symbol="m²"
            />
          ),
        },
        {
          title: 'podl. plocha',
          dataIndex: 'podlahovaplocha',
          width: 120,
          className: 'text-right',
          sorter: numberSorter('podlahovaplocha'),
          ...rangeFilter('podlahovaplocha'),
          render: (v: number | null) => (
            <Money amount={v} decimalNumbers={0} symbol="m²" noData="---" />
          ),
        },
        {
          title: 'kanalizace',
          dataIndex: 'pripojenikanalizaceText',
          width: 80,
          sorter: stringSorter('pripojenikanalizaceText'),
          ...getColumnOptionFilterKanalizace('pripojenikanalizacekod'),
        },
        {
          title: 'plyn',
          dataIndex: 'pripojeniplynText',
          width: 80,
          sorter: stringSorter('pripojeniplynText'),
          ...getColumnOptionFilterPlyn('pripojeniplynkod'),
        },
        {
          title: 'vodovod',
          dataIndex: 'pripojenivodovodText',
          width: 80,
          sorter: stringSorter('pripojenivodovodText'),
          ...getColumnOptionFilterVodovod('pripojenivodovodkod'),
        },
        {
          title: 'vytápění',
          width: 80,
          dataIndex: 'zpusobvytapeniText',
          sorter: stringSorter('zpusobvytapeniText'),
          ...getColumnOptionFilterVytapeni('zpusobvytapenikod'),
        },
        {
          title: 'výtah',
          dataIndex: 'vybavenivytahemText',
          width: 70,
          sorter: stringSorter('vybavenivytahemText'),
          ...getColumnOptionFilterVytah('vybavenivytahemkod'),
        },
        {
          title: '',
          dataIndex: 'kod',
          key: 'action',
          width: 160,
          className: 'text-right',
          render: (kod: number) => (
            <PropertyListDetailPropertyDetailLink
              propertyCode={kod.toString()}
              propertyType={PROPERTY_TYPES.BUILDING}
              buttonText="Zobrazit detail"
            />
          ),
        },
      ],
      [
        getColumnOptionFilterDruhKonstrukce,
        getColumnOptionFilterKanalizace,
        getColumnOptionFilterPlyn,
        getColumnOptionFilterVodovod,
        getColumnOptionFilterVytah,
        getColumnOptionFilterVytapeni,
        getColumnOptionFilterZpusobVyuziti,
        rangeFilter,
        searchFilter,
      ]
    )

    if (error) return <Alert type="error" message={error} />

    return (
      <Table<Item>
        id={tableId}
        rowKey="key"
        size="small"
        loading={loading || !whereClause}
        locale={locale}
        dataSource={dataSource}
        columns={columns}
        scroll={scrollX}
        pagination={sparsePagination}
      />
    )
  }
